import { useCallback } from 'react';

import { useTrackReorderedLibraries } from '@air/analytics';
import { type LibraryWithPermissions } from '@air/api/types';
import { useToasts } from '@air/provider-toast';

import { type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { useLibrariesUpdatePositionsMutation } from '~/components/v2/hooks/queries/mutations/useLibrariesUpdatePositionsMutation';
import { calculateLibraryRearrangeData } from '~/components/v2/utils/libraries/calculateLibraryRearrangeData';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export const useRearrangeLibraries = () => {
  const { showToast } = useToasts();
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackReorderedLibraries } = useTrackReorderedLibraries();
  const { mutate: updatePositions } = useLibrariesUpdatePositionsMutation();

  const workspaceId = currentWorkspace?.id;

  const rearrangeLibraries = useCallback(
    ({
      libraries,
      draggedLibrary,
      adjacentItem,
    }: {
      libraries: LibraryWithPermissions[];
      draggedLibrary: LibraryWithPermissions;
      adjacentItem: RearrangableItem;
    }) => {
      if (!workspaceId) {
        showToast('No workspace found.');
        return;
      }

      const { nextItemId, previousItemId } = calculateLibraryRearrangeData(libraries, draggedLibrary, adjacentItem);

      const betweenIds: {
        moveBeforeId?: string;
        moveAfterId?: string;
      } = {};

      if (previousItemId) {
        betweenIds.moveAfterId = previousItemId;
      }

      if (nextItemId) {
        betweenIds.moveBeforeId = nextItemId;
      }

      // Only update positions if we have at least one anchor point.
      // If both are null, the library is either:
      // 1. Already in the target position (dropped on itself)
      // 2. The only library in the list
      // In these cases, no update is needed.
      if (betweenIds.moveAfterId || betweenIds.moveBeforeId) {
        updatePositions(
          {
            workspaceId,
            idsToMove: [draggedLibrary.id],
            betweenIds,
          },
          {
            onSuccess: () => {
              trackReorderedLibraries({
                library_id: draggedLibrary.id,
              });
            },
            onError: () => {
              showToast('There was an error updating library position.');
            },
          },
        );
      }
    },
    [workspaceId, updatePositions, trackReorderedLibraries, showToast],
  );

  return { rearrangeLibraries };
};
